/**
 * Pure session-runner state machine.
 *
 * Walks a pre-assembled {@link SessionPlan} block by block, exercise by
 * exercise. No React, no storage, no `Date.now()`/`Math.random()` — `now` is
 * always supplied by the caller (the UI boundary), and every action returns a
 * brand-new {@link RunnerState} rather than mutating its input. This mirrors
 * the purity contract of `@repo/hindi-shared`'s SRS/session modules.
 */
import type { Grade, SessionBlock, SessionPlan } from "@repo/hindi-shared";

/** A single graded result recorded as the learner completes an exercise. */
export interface RunnerResult {
  itemId: string;
  grade: Grade;
  /** The kind of the block the exercise belonged to, not the destination block. */
  blockKind: SessionBlock["kind"];
}

/** The runner's full state: where the learner is in the plan, and what they've done so far. */
export interface RunnerState {
  plan: SessionPlan;
  blockIndex: number;
  exerciseIndex: number;
  results: RunnerResult[];
  startedAt: number;
  finished: boolean;
}

export type RunnerAction =
  | { type: "complete-exercise"; itemId: string; grade: Grade; now: number }
  | { type: "skip-block"; now: number }
  | { type: "finish"; now: number };

/** Start a runner at the first block/exercise of `plan`, un-finished, no results yet. */
export function initRunner(plan: SessionPlan, now: number): RunnerState {
  return {
    plan,
    blockIndex: 0,
    exerciseIndex: 0,
    results: [],
    startedAt: now,
    finished: false,
  };
}

/** Index of the last block in `blocks` that is not the trailing "summary" block, or -1 if none. */
function lastNonSummaryBlockIndex(blocks: readonly SessionBlock[]): number {
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    if (blocks[i]!.kind !== "summary") return i;
  }
  return -1;
}

/**
 * Advance `blockIndex` forward past any blocks with zero exercises, starting
 * from `fromIndex`. Returns the resulting `{ blockIndex, finished }`: finished
 * becomes true once the search runs past the last non-summary block (i.e.
 * every remaining block, including the summary, is behind the learner).
 */
function skipEmptyBlocksFrom(
  blocks: readonly SessionBlock[],
  fromIndex: number,
): { blockIndex: number; finished: boolean } {
  const lastReal = lastNonSummaryBlockIndex(blocks);
  let index = fromIndex;
  while (index < blocks.length && blocks[index]!.exercises.length === 0) {
    index += 1;
  }
  // Finished once we've moved past the last non-summary block, or run off
  // the end of the plan entirely (defensive: an all-empty plan).
  const finished = index > lastReal || index >= blocks.length;
  return { blockIndex: Math.min(index, blocks.length - 1), finished };
}

export function runnerReducer(s: RunnerState, a: RunnerAction): RunnerState {
  if (s.finished) return s;

  switch (a.type) {
    case "complete-exercise": {
      const block = s.plan.blocks[s.blockIndex];
      if (block === undefined) return s;

      const results: RunnerResult[] = [
        ...s.results,
        { itemId: a.itemId, grade: a.grade, blockKind: block.kind },
      ];

      const nextExerciseIndex = s.exerciseIndex + 1;
      if (nextExerciseIndex < block.exercises.length) {
        return { ...s, exerciseIndex: nextExerciseIndex, results };
      }

      // Block complete — advance to the next block, skipping empties.
      const { blockIndex, finished } = skipEmptyBlocksFrom(
        s.plan.blocks,
        s.blockIndex + 1,
      );
      return { ...s, blockIndex, exerciseIndex: 0, results, finished };
    }

    case "skip-block": {
      const { blockIndex, finished } = skipEmptyBlocksFrom(
        s.plan.blocks,
        s.blockIndex + 1,
      );
      return { ...s, blockIndex, exerciseIndex: 0, finished };
    }

    case "finish":
      return { ...s, finished: true };

    default:
      return s;
  }
}

/**
 * Fraction of `results` graded ≥2 (Good/Easy — a "remembered" outcome).
 * Returns 0 when `results` is empty (no items reviewed, not 100% or NaN).
 */
export function sessionAccuracy(results: readonly RunnerResult[]): number {
  if (results.length === 0) return 0;
  const remembered = results.filter((r) => r.grade >= 2).length;
  return remembered / results.length;
}
